import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { GatewayClassName, getNiceTimeOnly, roundDecimals } from '@iotv/datamodel-core';
import { Milliseconds, Networks } from '@iotv/iotv-v3-types';
import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useContext, useEffect, useReducer, useState } from 'react';
import { StripedDataGrid, WrapLinkVOB } from '../../../../components/factories/DataGrid';
import { AutosizedTextField } from '../../../common/AutosizedTextField';
import { AWSGatewaysContext, defaultState, gatewayListReducer } from './Context';
import { getAllGatewaysInDB, getLastTwentyFourQuery, getLNCStatistics } from './queries';
export const AWSGatewayView = () => {
    const [state, dispatch] = useReducer(gatewayListReducer, defaultState);
    const { setUpdateDatabaseDetails, setUpdateLastDayDetails, setUpdateWirelessStatistics } = state.queries ?? {};
    const queryDetails = () => {
        getLNCStatistics().then((res) => {
            dispatch({ type: 'setUpdateWirelessStatistics', networksData: res });
        });
        /** Only Query Once */
        if (setUpdateDatabaseDetails === false) {
            getAllGatewaysInDB().then((res) => {
                const gatewayData = res?.Items;
                if (gatewayData !== undefined) {
                    dispatch({ type: 'setUpdateDatabaseDetails', gatewayData: gatewayData });
                }
            });
        }
        if (setUpdateLastDayDetails === false) {
            getLastTwentyFourQuery().then((res) => {
                if (res !== undefined && res !== null) {
                    dispatch({ type: 'setUpdateLastDayDetails', bucketedData: res });
                }
                else {
                    dispatch({ type: 'setUpdateLastDayDetails', bucketedData: null });
                }
            });
        }
    };
    useEffect(() => {
        queryDetails();
        const interval = setInterval(() => queryDetails(), Milliseconds.MINUTE);
        return () => clearInterval(interval);
    }, []);
    return (_jsx(AWSGatewaysContext.Provider, { value: { state, dispatch }, children: _jsx(AWSGatewayViewInternal, {}) }));
};
const gatewayStatsToFlatRow = (record) => {
    const { eui, name, RfRegion, GatewayEui, ConnectionStatus, LastUplinkReceivedAt, lastSeen, Description, networkKey, lncInstanceType, congested, congesting, totalAirTimeSecs, backhaul, droppedMsgs, uplinks, downlinks, devices, id, } = record;
    const time = LastUplinkReceivedAt ?? lastSeen;
    const parsedEUI = (GatewayEui ?? eui ?? id)?.toUpperCase();
    return {
        sk: `${GatewayClassName}:${parsedEUI}`,
        name: name,
        rfRegion: RfRegion,
        gatewayEui: parsedEUI,
        status: ConnectionStatus,
        lastActivityTime: time,
        description: Description,
        networkKey,
        lncInstanceType: networkKey === Networks.AWS_LPWAN ? lncInstanceType : undefined,
        congested,
        congesting,
        totalAirTimeSecs: totalAirTimeSecs ? roundDecimals(totalAirTimeSecs, 3) : undefined,
        backhaul,
        droppedMsgs,
        uplinks,
        downlinks,
        devices: devices ? roundDecimals(devices, 2) : undefined,
        id: id,
    };
};
const AWSGatewayViewInternal = () => {
    const { state } = useContext(AWSGatewaysContext);
    const [name, setName] = useState('');
    const [eui, setEUI] = useState('');
    const nameRegex = new RegExp(name, 'i');
    const euiRegex = new RegExp(eui, 'i');
    const gateways = Object.values(state.gateways)
        .map((g) => g?.combined)
        .filter((g) => g !== undefined)
        .filter((g) => {
        let nameFilter = true;
        let euiFilter = true;
        if (name.length)
            nameFilter = nameRegex.test(g.name ?? '');
        if (eui.length)
            euiFilter = euiRegex.test(g.id ?? '');
        return nameFilter && euiFilter;
    });
    // eslint-disable-next-line react-hooks/purity
    const now = Date.now();
    const iotCoreDatagridProps = {
        columns: [
            { field: 'name', headerName: 'Name', width: 200 },
            { field: 'rfRegion', headerName: 'RF Region', width: 100 },
            { field: 'gatewayEui', headerName: 'Gateway EUI', width: 160, renderCell: (p) => WrapLinkVOB(p, 'sk', undefined) },
            { field: 'status', headerName: 'Status', width: 110 },
            {
                field: 'lastActivityTime',
                headerName: 'Last Activity',
                width: 200,
                valueFormatter: (v) => (v ? dayjs(v).format('YYYY/MM/DD - HH:MM:ss') : null),
            },
            { field: 'description', headerName: 'Description', width: 200 },
            { field: 'networkKey', headerName: 'Network', width: 120 },
            { field: 'lncInstanceType', headerName: 'LNC Type', width: 80 },
            { field: 'congested', headerName: 'Congested', width: 90 },
            { field: 'congesting', headerName: 'Congesting', width: 90 },
            { field: 'totalAirTimeSecs', headerName: 'Air Time (s)', width: 90 },
            { field: 'backhaul', headerName: 'Backhaul', width: 80 },
            { field: 'droppedMsgs', headerName: 'Dropped Messages (24hr)', width: 120 },
            { field: 'uplinks', headerName: 'Uplinks (24hr)', width: 120 },
            { field: 'downlinks', headerName: 'Downlinks (24hr)', width: 120 },
            { field: 'devices', headerName: 'Devices', width: 80 },
            { field: 'id', headerName: 'ID', width: 160 },
        ],
        rows: gateways.map(gatewayStatsToFlatRow),
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        density: 'compact',
        rowHeight: 32,
        rowSelection: false,
        initialState: { sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } },
    };
    return (_jsxs(Stack, { gap: 1, children: [_jsx(Typography, { variant: "h5", children: "IoT Wireless Gateways" }), _jsxs(Stack, { direction: "row", alignItems: "baseline", gap: 1, children: [_jsx(Typography, { variant: "h6", children: "Automatically updates every minute" }), _jsxs(Typography, { children: ["Last updated at ", getNiceTimeOnly(now)] })] }), _jsxs(Stack, { direction: "row", gap: 1, children: [_jsx(AutosizedTextField, { label: "Search By Name", value: name, onChange: (e) => setName(e.target.value) }), _jsx(AutosizedTextField, { label: "Search By EUI", value: eui, onChange: (e) => setEUI(e.target.value) })] }), _jsx(StripedDataGrid, { ...iotCoreDatagridProps })] }));
};
